import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ALIQUOTE, CANALI, calcIva, fmtEUR, parseNum, toInput, todayISO } from "@/lib/format";
import { Field, SimpleSelect, btnGhost, btnPrimary, inputCls } from "@/components/app/common";
import { CategoriaCombobox, catOpts } from "@/components/app/CategoriaCombobox";

const empty = { nome: "", categoria: "", canale: "Generale", imponibile: "", aliquota_iva: "22", giorno: "1", data_inizio: todayISO(), data_fine: "" };

export function RicorrenteDialog({ open, onOpenChange, item, categorie, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF(item ? { ...item, imponibile: toInput(item.imponibile), aliquota_iva: String(item.aliquota_iva), giorno: String(item.giorno), data_fine: item.data_fine || "" } : { ...empty, data_inizio: todayISO() });
  }, [open, item]);

  const imp = parseNum(f.imponibile);
  const { iva, totale } = calcIva(imp || 0, Number(f.aliquota_iva));

  const save = async () => {
    if (!f.nome.trim()) return toast.error("Inserisci un nome");
    if (!f.categoria) return toast.error("Seleziona una categoria");
    if (imp == null) return toast.error("Inserisci un imponibile valido");
    const giorno = Number(f.giorno);
    if (!(giorno >= 1 && giorno <= 31)) return toast.error("Il giorno deve essere tra 1 e 31");
    setSaving(true);
    const body = { nome: f.nome.trim(), categoria: f.categoria, canale: f.canale, imponibile: imp, aliquota_iva: Number(f.aliquota_iva), giorno, data_inizio: f.data_inizio, data_fine: f.data_fine || null, attiva: item ? item.attiva : true };
    try {
      const { data } = item ? await api.put(`/ricorrenti/${item.id}`, body) : await api.post("/ricorrenti", body);
      toast.success(item ? "Spesa ricorrente aggiornata" : "Spesa ricorrente creata", { description: data.generati ? `${data.generati} movimenti generati` : undefined });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="ricorrente-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">{item ? "Modifica spesa ricorrente" : "Nuova spesa ricorrente"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Nome"><Input data-testid="ricorrente-nome-input" value={f.nome} onChange={(e) => set("nome")(e.target.value)} className={inputCls} placeholder="Es. Affitto negozio" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Categoria"><CategoriaCombobox testId="ricorrente-categoria-select" tipo="costo" value={f.categoria} onChange={set("categoria")} placeholder="Scegli o crea" options={catOpts(categorie)} /></Field>
            <Field label="Canale"><SimpleSelect testId="ricorrente-canale-select" value={f.canale} onChange={set("canale")} options={CANALI.map((c) => ({ value: c, label: c }))} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Imponibile (€)"><Input data-testid="ricorrente-imponibile-input" inputMode="decimal" value={f.imponibile} onChange={(e) => set("imponibile")(e.target.value)} className={inputCls} placeholder="0,00" /></Field>
            <Field label="IVA"><SimpleSelect testId="ricorrente-aliquota-select" value={f.aliquota_iva} onChange={set("aliquota_iva")} options={ALIQUOTE.map((a) => ({ value: String(a), label: `${a}%` }))} /></Field>
            <Field label="Giorno del mese"><Input data-testid="ricorrente-giorno-input" type="number" min={1} max={31} value={f.giorno} onChange={(e) => set("giorno")(e.target.value)} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data inizio"><Input data-testid="ricorrente-inizio-input" type="date" value={f.data_inizio} onChange={(e) => set("data_inizio")(e.target.value)} className={inputCls} /></Field>
            <Field label="Data fine (facoltativa)"><Input data-testid="ricorrente-fine-input" type="date" value={f.data_fine} onChange={(e) => set("data_fine")(e.target.value)} className={inputCls} /></Field>
          </div>
          <div className="rounded-xl bg-[#FAF7F5] p-4 text-sm flex justify-between" data-testid="ricorrente-preview">
            <span className="text-[#756B64]">IVA {fmtEUR(iva)}</span>
            <span className="font-semibold">Totale mensile {fmtEUR(totale)}</span>
          </div>
          {item && <p className="text-xs text-[#756B64]">Le modifiche all'importo valgono solo per i mesi futuri: i movimenti già generati restano invariati.</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)} data-testid="ricorrente-cancel-button">Annulla</Button>
          <Button className={btnPrimary} onClick={save} disabled={saving} data-testid="ricorrente-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
